import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

export default function AdminCategoriesPage() {
  const { token } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('📦');
  const [categoryDiscountPct, setCategoryDiscountPct] = useState(0);
  const [description, setDescription] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    loadCategories();
  }, [token]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/categories');
      if (res.ok) setCategories(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback({ type: '', message: '' });
    try {
      const isEditing = Boolean(editingCategory);
      const res = await fetch(isEditing ? `/api/categories/${editingCategory.id}` : '/api/categories', {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify({ name, icon, category_discount_pct: categoryDiscountPct, description }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || `Unable to ${isEditing ? 'update' : 'create'} category.`);

      resetForm();
      setFeedback({ type: 'success', message: isEditing ? 'Category updated.' : 'Category added.' });
      await loadCategories();
    } catch (err) {
      setFeedback({ type: 'danger', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setName('');
    setIcon('📦');
    setCategoryDiscountPct(0);
    setDescription('');
  };

  const startEditing = (category) => {
    setEditingCategory(category);
    setName(category.name || '');
    setIcon(category.icon || '📦');
    setCategoryDiscountPct(category.category_discount_pct || 0);
    setDescription(category.description || '');
    window.requestAnimationFrame(() => document.getElementById('category-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const handleDeleteCategory = async (category) => {
    const hasProducts = Number(category.product_count || 0) > 0;
    const warning = hasProducts
      ? `Remove “${category.name}”? The ${category.product_count} product${category.product_count === 1 ? '' : 's'} will stay in the catalogue but become uncategorised.`
      : `Remove “${category.name}”? This cannot be undone.`;
    if (!window.confirm(warning)) return;

    setDeletingCategoryId(category.id);
    setFeedback({ type: '', message: '' });
    try {
      const res = await fetch(`/api/categories/${category.id}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to remove category.');
      if (editingCategory?.id === category.id) resetForm();
      setFeedback({ type: 'success', message: 'Category removed. Products were kept.' });
      await loadCategories();
    } catch (err) {
      setFeedback({ type: 'danger', message: err.message });
    } finally {
      setDeletingCategoryId(null);
    }
  };

  return (
    <div className="container-fluid py-4">
      <h2 className="brand-font mb-4">Category Management & Category Discounts</h2>

      {feedback.message && <div className={`alert alert-${feedback.type} py-2`} role="alert">{feedback.message}</div>}

      <div className="row g-4">
        <div className="col-12 col-lg-5">
          <form id="category-editor" onSubmit={handleCreateCategory} className="p-4 border rounded bg-card shadow-sm">
            <h5 className="font-weight-bold mb-3">{editingCategory ? 'Edit Category' : 'Add Category'}</h5>

            <div className="mb-3">
              <label className="form-label small font-weight-bold" htmlFor="category-name">Category Name *</label>
              <input
                id="category-name"
                type="text"
                className="form-control"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label small font-weight-bold" htmlFor="category-icon">Icon Emoji</label>
              <input
                id="category-icon"
                type="text"
                className="form-control"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label small font-weight-bold" htmlFor="category-discount">Category Discount %</label>
              <input
                id="category-discount"
                type="number"
                className="form-control"
                min="0"
                max="100"
                value={categoryDiscountPct}
                onChange={(e) => setCategoryDiscountPct(e.target.value)}
              />
              <span className="text-muted small">Applies to all products under this category</span>
            </div>

            <div className="mb-3">
              <label className="form-label small font-weight-bold" htmlFor="category-description">Description</label>
              <textarea
                id="category-description"
                className="form-control"
                rows="2"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button type="submit" className="btn btn-primary-custom flex-grow-1 rounded-pill" disabled={saving}>
                {saving ? 'Saving…' : editingCategory ? 'Update Category' : '+ Save Category'}
              </button>
              {editingCategory && <button type="button" className="btn btn-outline-custom rounded-pill" onClick={resetForm} disabled={saving}>Cancel</button>}
            </div>
          </form>
        </div>

        <div className="col-12 col-lg-7">
          <div className="border rounded bg-card p-3 shadow-sm">
            <h5 className="font-weight-bold mb-3">Existing Categories</h5>
            {loading ? (
              <div className="spinner-border text-primary" role="status"></div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle admin-category-table">
                  <thead>
                    <tr>
                      <th>Icon</th>
                      <th>Category Name</th>
                      <th>Slug</th>
                      <th>Cat Discount %</th>
                      <th>Products</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c) => (
                      <tr key={c.id}>
                        <td data-label="Icon" className="fs-4">{c.icon || '📦'}</td>
                        <td data-label="Category name" className="font-weight-bold">{c.name}</td>
                        <td data-label="Slug" className="small text-muted">{c.slug}</td>
                        <td data-label="Category discount">
                          <span className="badge bg-danger">{c.category_discount_pct}%</span>
                        </td>
                        <td data-label="Products">
                          <span className="badge bg-secondary">{c.product_count || 0}</span>
                        </td>
                        <td data-label="Actions">
                          <div className="admin-category-actions">
                            <button type="button" className="btn btn-sm btn-outline-custom" onClick={() => startEditing(c)}>Edit</button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteCategory(c)} disabled={deletingCategoryId === c.id}>
                              {deletingCategoryId === c.id ? 'Removing…' : 'Remove'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
